import { FormEvent, useEffect, useMemo, useState } from 'react';
import type { Configuracion, FacturacionApi, Sucursal, SucursalInput, Ubicacion } from '../api/types';
import { formatearCoordenadas, LIMITES, RX } from '../validacion';
import { Alerta, Boton, Campo, ErrorApi, Guia, Pill } from './ui';

interface Props {
  api: FacturacionApi;
  config: Configuracion;
  onGuardado: (c: Configuracion) => void;
}

export function SucursalesSeccion({ api, config, onGuardado }: Props) {
  const [editando, setEditando] = useState<Sucursal | 'nueva' | null>(null);
  const [ubicaciones, setUbicaciones] = useState<Ubicacion[] | null>(null);
  const [errorUbic, setErrorUbic] = useState<unknown>(null);

  const registrado = !!config.emisor?.alanubeIdCompany;
  const sinSucursales = config.sucursales.length === 0;

  useEffect(() => {
    if (!registrado || ubicaciones) return;
    api.ubicaciones().then(setUbicaciones).catch(setErrorUbic);
  }, [api, registrado, ubicaciones]);

  if (!registrado) {
    return (
      <section className="seccion">
      <Guia
        id="sucursales"
        titulo="Cómo registrar sus sucursales"
        pasos={[
          <>Registre primero su <b>sucursal principal</b>. Es la que se usa en los comprobantes cuando no se indica otra.</>,
          <><b>Código de sucursal</b>: 4 caracteres que identifican la sucursal en sus comprobantes (por ejemplo 0000).</>,
          <><b>Punto de facturación</b>: 3 dígitos (por ejemplo 001), distinto de 000.</>,
          <>Complete dirección, teléfono (999-9999 o 9999-9999), provincia · distrito · corregimiento y coordenadas. Si está en el local, use <b>Usar mi ubicación</b>.</>,
          <><b>Numeración</b>: si nunca emitió facturas electrónicas, deje 0. Si viene de otro proveedor, escriba el último número usado de cada tipo para no repetirlo.</>,
        ]}
      />
        <header className="seccion__cab">
          <h2>Sucursales</h2>
        </header>
        <Alerta tipo="info" titulo="Primero registre el emisor">
          Complete y guarde primero los datos del emisor y los certificados.
        </Alerta>
      </section>
    );
  }

  return (
    <section className="seccion">
      <Guia
        id="sucursales"
        titulo="Cómo registrar sus sucursales"
        pasos={[
          <>Registre primero su <b>sucursal principal</b>. Es la que se usa en los comprobantes cuando no se indica otra.</>,
          <><b>Código de sucursal</b>: 4 caracteres que identifican la sucursal en sus comprobantes (por ejemplo 0000).</>,
          <><b>Punto de facturación</b>: 3 dígitos (por ejemplo 001), distinto de 000.</>,
          <>Complete dirección, teléfono (999-9999 o 9999-9999), provincia · distrito · corregimiento y coordenadas. Si está en el local, use <b>Usar mi ubicación</b>.</>,
          <><b>Numeración</b>: si nunca emitió facturas electrónicas, deje 0. Si viene de otro proveedor, escriba el último número usado de cada tipo para no repetirlo.</>,
        ]}
      />
      <header className="seccion__cab">
        <div>
          <h2>Sucursales y puntos de facturación</h2>
          <p className="texto-sec">
            La DGI exige la ubicación y el contacto de la sucursal en cada comprobante.
          </p>
        </div>
        {!editando && (
          <Boton onClick={() => setEditando('nueva')}>{sinSucursales ? 'Registrar sucursal principal' : 'Agregar sucursal'}</Boton>
        )}
      </header>

      <ErrorApi error={errorUbic} />

      {editando && (
        <SucursalForm
          api={api}
          config={config}
          sucursal={editando === 'nueva' ? null : editando}
          esPrincipal={editando === 'nueva' ? sinSucursales : editando.tipo === 'main'}
          ubicaciones={ubicaciones}
          onCancelar={() => setEditando(null)}
          onGuardado={(c) => {
            onGuardado(c);
            setEditando(null);
          }}
        />
      )}

      {sinSucursales && !editando ? (
        <div className="vacio">Aún no hay sucursales. Sin la sucursal principal no se pueden emitir comprobantes.</div>
      ) : (
        config.sucursales.length > 0 && (
          <div className="tabla-scroll">
            <table className="tabla">
              <thead>
                <tr>
                  <th>Código</th>
                  <th>Dirección</th>
                  <th>Teléfono</th>
                  <th>Ubicación</th>
                  <th>Punto fact.</th>
                  <th className="num">Últ. factura</th>
                  <th className="num">Últ. NC</th>
                  <th className="num">Últ. ND</th>
                  <th>Estado</th>
                  <th aria-label="Acciones" />
                </tr>
              </thead>
              <tbody>
                {config.sucursales.map((s) => (
                  <tr key={s.id}>
                    <td>
                      <span className="mono">{s.codigo}</span>
                      {s.tipo === 'main' && <Pill tono="neutro">Principal</Pill>}
                    </td>
                    <td>{s.direccion}</td>
                    <td className="mono">{s.telefono}</td>
                    <td>{s.ubicacionTexto ?? s.ubicacion ?? '—'}</td>
                    <td className="mono">{s.puntoFacturacion}</td>
                    <td className="num mono">{s.numeracion.factura}</td>
                    <td className="num mono">{s.numeracion.notaCredito}</td>
                    <td className="num mono">{s.numeracion.notaDebito}</td>
                    <td>
                      {!s.alanubeIdOffice ? (
                        <Pill tono="error">No sincronizada</Pill>
                      ) : s.activa ? (
                        <Pill tono="ok">Activa</Pill>
                      ) : (
                        <Pill tono="neutro">Inactiva</Pill>
                      )}
                    </td>
                    <td>
                      <Boton tipo="texto" onClick={() => setEditando(s)} deshabilitado={!!editando}>
                        Editar
                      </Boton>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </section>
  );
}

type Errores = Partial<Record<keyof SucursalInput | 'numFactura' | 'numNotaCredito' | 'numNotaDebito', string>>;
type ClaveNum = keyof SucursalInput['numeracion'];
const NUMERACIONES: { clave: ClaveNum; error: 'numFactura' | 'numNotaCredito' | 'numNotaDebito'; etiqueta: string }[] = [
  { clave: 'factura', error: 'numFactura', etiqueta: 'Facturas' },
  { clave: 'notaCredito', error: 'numNotaCredito', etiqueta: 'Notas de crédito' },
  { clave: 'notaDebito', error: 'numNotaDebito', etiqueta: 'Notas de débito' },
];

function SucursalForm(p: {
  api: FacturacionApi;
  config: Configuracion;
  sucursal: Sucursal | null;
  esPrincipal: boolean;
  ubicaciones: Ubicacion[] | null;
  onCancelar: () => void;
  onGuardado: (c: Configuracion) => void;
}) {
  const s = p.sucursal;
  const [d, setD] = useState<SucursalInput>(() =>
    s
      ? {
          codigo: s.codigo,
          direccion: s.direccion,
          telefono: s.telefono,
          coordenadas: s.coordenadas,
          ubicacion: s.ubicacion,
          ubicacionTexto: s.ubicacionTexto,
          email: s.email,
          puntoFacturacion: s.puntoFacturacion,
          numeracion: { ...s.numeracion },
          activa: s.activa,
        }
      : {
          codigo: p.esPrincipal ? '0000' : '',
          direccion: p.esPrincipal ? (p.config.precarga.direccion ?? '').slice(0, LIMITES.direccionMax) : '',
          telefono: '',
          coordenadas: '',
          ubicacion: null,
          ubicacionTexto: null,
          email: null,
          puntoFacturacion: siguientePunto(p.config.sucursales),
          numeracion: { factura: 0, notaCredito: 0, notaDebito: 0 },
          activa: true,
        },
  );
  const [errores, setErrores] = useState<Errores>({});
  const [guardando, setGuardando] = useState(false);
  const [errorApi, setErrorApi] = useState<unknown>(null);
  const [geoMsg, setGeoMsg] = useState<string | null>(null);

  const set = <K extends keyof SucursalInput>(k: K, v: SucursalInput[K]) => {
    setD((x) => ({ ...x, [k]: v }));
    setErrores((x) => ({ ...x, [k]: undefined }));
  };

  function usarUbicacionActual() {
    if (!navigator.geolocation) return setGeoMsg('El navegador no permite obtener la ubicación.');
    setGeoMsg('Obteniendo ubicación…');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const c = formatearCoordenadas(pos.coords.latitude, pos.coords.longitude);
        if (!c) return setGeoMsg('La ubicación obtenida no está en el formato admitido (latitud norte, longitud oeste).');
        set('coordenadas', c);
        setGeoMsg(null);
      },
      () => setGeoMsg('No se pudo obtener la ubicación. Ingrésela manualmente.'),
      { enableHighAccuracy: true, timeout: 10_000 },
    );
  }

  function validar(): Errores {
    const e: Errores = {};
    if (!RX.codigoSucursal.test(d.codigo)) e.codigo = 'Exactamente 4 caracteres (ej. 0000).';
    if (!RX.puntoFacturacion.test(d.puntoFacturacion)) e.puntoFacturacion = '3 dígitos, distinto de 000.';
    if (!d.direccion.trim()) e.direccion = 'Obligatoria.';
    else if (d.direccion.length > LIMITES.direccionMax) e.direccion = `Máximo ${LIMITES.direccionMax} caracteres.`;
    if (!RX.telefono.test(d.telefono.trim())) e.telefono = 'Formato 999-9999 o 9999-9999.';
    if (!RX.coordenadas.test(d.coordenadas.trim()) || d.coordenadas.length > 22)
      e.coordenadas = 'Formato +8.9824000,-79.5199000 (máx. 7 decimales).';
    if (!d.ubicacion) e.ubicacion = 'Seleccione provincia, distrito y corregimiento.';
    if (d.email && (!RX.email.test(d.email) || d.email.length > LIMITES.emailSucursalMax))
      e.email = `Correo inválido (máx. ${LIMITES.emailSucursalMax} caracteres).`;
    for (const n of NUMERACIONES) {
      const v = d.numeracion[n.clave];
      if (!Number.isInteger(v) || v < 0 || v > LIMITES.numeracionMax) e[n.error] = 'Número entre 0 y 9999999999.';
      else if (s && v < s.numeracion[n.clave]) e[n.error] = `No puede ser menor a ${s.numeracion[n.clave]}.`;
    }
    if (p.config.sucursales.some((x) => x.id !== s?.id && x.codigo === d.codigo)) e.codigo = 'Ya existe otra sucursal con este código.';
    return e;
  }

  async function guardar(ev: FormEvent) {
    ev.preventDefault();
    const e = validar();
    setErrores(e);
    if (Object.values(e).some(Boolean)) return;
    setGuardando(true);
    setErrorApi(null);
    const datos: SucursalInput = {
      ...d,
      codigo: d.codigo.trim(),
      direccion: d.direccion.trim(),
      telefono: d.telefono.trim(),
      coordenadas: d.coordenadas.trim(),
      email: d.email?.trim() || null,
    };
    try {
      const c = s ? await p.api.actualizarSucursal(p.config.idEmpresa, s.id, datos) : await p.api.crearSucursal(p.config.idEmpresa, datos);
      p.onGuardado(c);
    } catch (err) {
      setErrorApi(err);
    } finally {
      setGuardando(false);
    }
  }

  return (
    <form className="panel-form" onSubmit={guardar} noValidate>
      <h3>
        {s ? `Editar sucursal ${s.codigo}` : p.esPrincipal ? 'Nueva sucursal principal' : 'Nueva sucursal'}
      </h3>

      <div className="grid-3">
        <Campo etiqueta="Código de sucursal" error={errores.codigo} ayuda="4 caracteres, ej. 0000">
          {(id) => <input id={id} className="mono" maxLength={4} value={d.codigo} onChange={(e) => set('codigo', e.target.value)} />}
        </Campo>
        <Campo etiqueta="Punto de facturación" error={errores.puntoFacturacion} ayuda="3 dígitos, ej. 001">
          {(id) => (
            <input
              id={id}
              className="mono"
              inputMode="numeric"
              maxLength={3}
              value={d.puntoFacturacion}
              onChange={(e) => set('puntoFacturacion', e.target.value.replace(/\D/g, ''))}
            />
          )}
        </Campo>
        <Campo etiqueta="Teléfono" error={errores.telefono} ayuda="Formato 999-9999 o 9999-9999">
          {(id) => <input id={id} className="mono" inputMode="tel" value={d.telefono} onChange={(e) => set('telefono', e.target.value)} />}
        </Campo>
      </div>

      <Campo etiqueta="Dirección" error={errores.direccion} contador={{ actual: d.direccion.length, max: LIMITES.direccionMax }}>
        {(id) => <input id={id} value={d.direccion} onChange={(e) => set('direccion', e.target.value)} />}
      </Campo>

      <div className="grid-2">
        <Campo etiqueta="Provincia · Distrito · Corregimiento" error={errores.ubicacion} ayuda="Catálogo oficial de la DGI.">
          {(id) => (
            <UbicacionSelector
              id={id}
              ubicaciones={p.ubicaciones}
              valor={d.ubicacion}
              texto={d.ubicacionTexto}
              onChange={(u) => {
                setD((x) => ({ ...x, ubicacion: u?.codigo ?? null, ubicacionTexto: u?.valor ?? null }));
                setErrores((x) => ({ ...x, ubicacion: undefined }));
              }}
            />
          )}
        </Campo>
        <Campo
          etiqueta="Coordenadas"
          error={errores.coordenadas}
          ayuda={geoMsg ?? 'Latitud,longitud. Ej. +8.9824000,-79.5199000'}
        >
          {(id) => (
            <div className="input-con-boton">
              <input id={id} className="mono" maxLength={22} value={d.coordenadas} onChange={(e) => set('coordenadas', e.target.value)} />
              <Boton tipo="secundario" onClick={usarUbicacionActual}>
                Usar mi ubicación
              </Boton>
            </div>
          )}
        </Campo>
      </div>

      <div className="grid-3">
        <Campo etiqueta="Correo de la sucursal" opcional error={errores.email}>
          {(id) => <input id={id} type="email" maxLength={50} value={d.email ?? ''} onChange={(e) => set('email', e.target.value || null)} />}
        </Campo>
        {!p.esPrincipal && (
          <Campo etiqueta="Estado">
            {() => (
              <label className="switch">
                <input type="checkbox" checked={d.activa} onChange={(e) => set('activa', e.target.checked)} />
                <span>{d.activa ? 'Activa' : 'Inactiva'}</span>
              </label>
            )}
          </Campo>
        )}
      </div>

      <fieldset className="bloque bloque--compacto">
        <legend>Última numeración utilizada por tipo de documento</legend>
        <p className="texto-sec">
          Cada tipo de documento lleva su propio consecutivo de 10 dígitos en este punto de facturación.{' '}
          {s ? 'Sólo puede aumentar.' : 'Deje 0 si nunca emitió con este punto. Si viene de otro proveedor, indique el último número que usó.'}
        </p>
        <div className="grid-3">
          {NUMERACIONES.map((n) => (
            <Campo key={n.clave} etiqueta={n.etiqueta} error={errores[n.error]}>
              {(id) => (
                <input
                  id={id}
                  className="mono"
                  inputMode="numeric"
                  maxLength={10}
                  value={String(d.numeracion[n.clave])}
                  onChange={(e) => {
                    const v = Number(e.target.value.replace(/\D/g, '') || 0);
                    setD((x) => ({ ...x, numeracion: { ...x.numeracion, [n.clave]: v } }));
                    setErrores((x) => ({ ...x, [n.error]: undefined }));
                  }}
                />
              )}
            </Campo>
          ))}
        </div>
      </fieldset>

      <ErrorApi error={errorApi} />
      <div className="acciones">
        <Boton tipo="secundario" onClick={p.onCancelar} deshabilitado={guardando}>
          Cancelar
        </Boton>
        <Boton submit cargando={guardando}>
          {s ? 'Guardar sucursal' : 'Registrar sucursal'}
        </Boton>
      </div>
    </form>
  );
}

/** Buscador sobre el catálogo unificado de la DGI (código "8-8-7", valor "Panamá-Panamá-Bella Vista"). */
function UbicacionSelector(p: {
  id: string;
  ubicaciones: Ubicacion[] | null;
  valor: string | null;
  texto: string | null;
  onChange: (u: Ubicacion | null) => void;
}) {
  const [filtro, setFiltro] = useState('');
  const [abierto, setAbierto] = useState(false);

  const resultados = useMemo(() => {
    if (!p.ubicaciones) return [];
    const q = normalizar(filtro);
    const lista = q ? p.ubicaciones.filter((u) => normalizar(u.valor).includes(q) || u.codigo.startsWith(filtro.trim())) : p.ubicaciones;
    return lista.slice(0, 60);
  }, [p.ubicaciones, filtro]);

  if (!p.ubicaciones) return <input id={p.id} disabled placeholder="Cargando catálogo…" />;

  if (p.valor && !abierto) {
    return (
      <div className="ubic-seleccionada">
        <span>
          {(p.texto ?? '').split('-').join(' › ')} <span className="mono texto-sec">({p.valor})</span>
        </span>
        <Boton tipo="texto" onClick={() => setAbierto(true)}>
          Cambiar
        </Boton>
      </div>
    );
  }

  return (
    <div className="combo">
      <input
        id={p.id}
        autoFocus={abierto}
        value={filtro}
        placeholder="Escriba corregimiento, distrito o provincia"
        role="combobox"
        aria-expanded
        aria-controls={p.id + '-lista'}
        onChange={(e) => setFiltro(e.target.value)}
      />
      <ul id={p.id + '-lista'} role="listbox" className="combo__lista">
        {resultados.length === 0 && <li className="combo__vacio">Sin resultados</li>}
        {resultados.map((u) => (
          <li key={u.codigo}>
            <button
              type="button"
              role="option"
              aria-selected={u.codigo === p.valor}
              onClick={() => {
                p.onChange(u);
                setAbierto(false);
                setFiltro('');
              }}
            >
              {u.valor.split('-').join(' › ')} <span className="mono texto-sec">{u.codigo}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}

function siguientePunto(sucursales: Sucursal[]): string {
  const usados = new Set(sucursales.map((s) => s.puntoFacturacion));
  for (let i = 1; i < 1000; i++) {
    const p = String(i).padStart(3, '0');
    if (!usados.has(p)) return p;
  }
  return '';
}

const normalizar = (s: string) =>
  s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .trim();
